import { useState } from 'react';
import axios from 'axios';
import Modal, { ModalButton } from '../dm/Modal';
import { useServers } from './ServersContext';

const MESSAGE_CATEGORIES = [
  { id: 'spam', label: 'Spam' },
  { id: 'harassment', label: 'Harassment or hate' },
  { id: 'other', label: 'Something else' },
];
const SERVER_CATEGORIES = [
  { id: 'spam', label: 'Spam or scam' },
  { id: 'harassment', label: 'Harassment or hate' },
  { id: 'illegal', label: 'Illegal content' },
  { id: 'other', label: 'Something else' },
];

function ReportForm({ title, intro, categories, onSubmit, sentText, onClose }) {
  const [category, setCategory] = useState(null);
  const [reason, setReason] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | sent
  const [error, setError] = useState('');

  const submit = async () => {
    setState('sending');
    setError('');
    try {
      await onSubmit({ category, reason });
      setState('sent');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not send the report. Please try again.');
      setState('idle');
    }
  };

  if (state === 'sent') {
    return (
      <Modal title="Thanks for reporting" onClose={onClose} footer={<ModalButton primary onClick={onClose}>Done</ModalButton>}>
        <p className="m-0 text-sm leading-relaxed text-text-muted">{sentText}</p>
      </Modal>
    );
  }
  return (
    <Modal
      title={title}
      onClose={onClose}
      footer={<>
        <ModalButton onClick={onClose}>Cancel</ModalButton>
        <ModalButton danger disabled={!category || state === 'sending'} onClick={submit}>{state === 'sending' ? 'Sending…' : 'Report'}</ModalButton>
      </>}
    >
      <p className="mb-3 mt-0 text-[13px] leading-relaxed text-text-dim">{intro}</p>
      <div className="mb-3 flex flex-col gap-1.5">
        {categories.map(c => (
          <label
            key={c.id}
            className={`flex cursor-pointer items-center gap-3 rounded-sm border border-solid px-3 py-2.5 text-sm ${
              category === c.id ? 'border-purple bg-purple-glow text-text' : 'border-border text-text-muted hover:bg-surface-3'
            }`}
          >
            <input type="radio" name="report-category" checked={category === c.id} onChange={() => setCategory(c.id)} className="accent-[#9b5de5]" />
            {c.label}
          </label>
        ))}
      </div>
      <textarea
        value={reason}
        onChange={e => setReason(e.target.value.slice(0, 500))}
        placeholder="Add details (optional)"
        rows={3}
        className="box-border w-full resize-none rounded-sm border border-solid border-border bg-surface-3 px-3 py-2 font-body text-sm text-text outline-none placeholder:text-text-dim focus:border-purple"
      />
      {error && <div className="mt-2 text-xs text-red">{error}</div>}
    </Modal>
  );
}

// A message on a server goes to that server's moderators, not to BLOBL
export function ReportMessageDialog({ nodeId, messageId, onClose }) {
  const { conn } = useServers();
  return (
    <ReportForm
      title="Report message"
      intro="The report goes to the moderators of this server, together with the message and up to 10 messages before it. For illegal content or a problem with the whole server, report the server to BLOBL instead (server menu)."
      categories={MESSAGE_CATEGORIES}
      onSubmit={({ category, reason }) => conn(nodeId).post('/api/reports', { message_id: messageId, category, reason })}
      sentText="The moderators of this server will review it."
      onClose={onClose}
    />
  );
}

// The whole server, to the BLOBL moderators (they can block it)
export function ReportServerDialog({ nodeId, name, onClose }) {
  return (
    <ReportForm
      title={`Report ${name || 'server'}`}
      intro="This goes to the BLOBL moderators, who can block the server. They can't see messages on it: describe what is wrong."
      categories={SERVER_CATEGORIES}
      onSubmit={({ category, reason }) => axios.post('/api/reports/server', { node_id: nodeId, category, reason }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })}
      sentText="The BLOBL moderators will review this server."
      onClose={onClose}
    />
  );
}
